import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import promotionService, { Promotion, PromotionType, PromotionScope } from '@services/promotionService';

interface PromotionFormProps {
  promotion: Promotion | null;
  onSuccess: () => void;
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white text-sm';
const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';

function generateCode() {
  return Math.random().toString(36).toUpperCase().slice(2, 10);
}

const PromotionForm = ({ promotion, onSuccess }: PromotionFormProps) => {
  const queryClient = useQueryClient();

  const [formData, setFormData] = useState({
    name: promotion?.name ?? '',
    code: promotion?.code ?? '',
    type: (promotion?.type ?? 'PERCENTAGE') as PromotionType,
    value: promotion?.value ?? 0,
    minOrderAmount: promotion?.minOrderAmount ?? '',
    maxDiscountAmount: promotion?.maxDiscountAmount ?? '',
    usageLimit: promotion?.usageLimit ?? '',
    perUserLimit: promotion?.perUserLimit ?? 1,
    startsAt: promotion?.startsAt ? promotion.startsAt.slice(0, 16) : '',
    expiresAt: promotion?.expiresAt ? promotion.expiresAt.slice(0, 16) : '',
    isActive: promotion?.isActive ?? true,
    appliesTo: (promotion?.appliesTo ?? 'ALL') as PromotionScope,
    autoApply: promotion?.autoApply ?? false,
    description: promotion?.description ?? '',
  });

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = {
        ...formData,
        value: Number(formData.value),
        minOrderAmount: formData.minOrderAmount !== '' ? Number(formData.minOrderAmount) : undefined,
        maxDiscountAmount: formData.maxDiscountAmount !== '' ? Number(formData.maxDiscountAmount) : undefined,
        usageLimit: formData.usageLimit !== '' ? Number(formData.usageLimit) : undefined,
        perUserLimit: Number(formData.perUserLimit),
        code: formData.code || undefined,
        expiresAt: formData.expiresAt || undefined,
      };
      if (promotion) {
        return promotionService.update(promotion.id, payload);
      }
      return promotionService.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['promotions'] });
      toast.success(promotion ? 'Promotion updated' : 'Promotion created');
      onSuccess();
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Failed to save promotion');
    },
  });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
  ) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate();
  };

  const isFreeShipping = formData.type === 'FREE_SHIPPING';

  return (
    <form onSubmit={handleSubmit} className="p-6 space-y-5">
      {/* Name */}
      <div>
        <label className={labelClass}>Name *</label>
        <input
          type="text"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
          className={inputClass}
          placeholder="e.g. Welcome 30% Off"
        />
      </div>

      {/* Code */}
      <div>
        <label className={labelClass}>Promo Code</label>
        <div className="flex gap-2">
          <input
            type="text"
            name="code"
            value={formData.code}
            onChange={handleChange}
            className={inputClass}
            placeholder="Leave empty for auto-apply deals"
          />
          <button
            type="button"
            onClick={() => setFormData((prev) => ({ ...prev, code: generateCode() }))}
            className="px-3 py-2 text-xs bg-indigo-50 text-indigo-600 hover:bg-indigo-100 dark:bg-indigo-900/20 dark:text-indigo-400 rounded-lg whitespace-nowrap border border-indigo-200 dark:border-indigo-800"
          >
            Generate
          </button>
        </div>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Leave blank for auto-applied deals (no code needed)
        </p>
      </div>

      {/* Type */}
      <div>
        <label className={labelClass}>Type *</label>
        <select name="type" value={formData.type} onChange={handleChange} className={inputClass}>
          <option value="PERCENTAGE">Percentage (%)</option>
          <option value="FIXED_AMOUNT">Fixed Amount (€)</option>
          <option value="FREE_SHIPPING">Free Shipping</option>
        </select>
      </div>

      {/* Value — hidden for FREE_SHIPPING */}
      {!isFreeShipping && (
        <div>
          <label className={labelClass}>
            {formData.type === 'PERCENTAGE' ? 'Discount Percentage *' : 'Discount Amount (€) *'}
          </label>
          <input
            type="number"
            name="value"
            value={formData.value}
            onChange={handleChange}
            required
            min={0}
            max={formData.type === 'PERCENTAGE' ? 100 : undefined}
            step={formData.type === 'PERCENTAGE' ? 1 : 0.01}
            className={inputClass}
            placeholder={formData.type === 'PERCENTAGE' ? '30' : '10.00'}
          />
        </div>
      )}

      {/* Max discount cap (only for PERCENTAGE) */}
      {formData.type === 'PERCENTAGE' && (
        <div>
          <label className={labelClass}>Maximum Discount Cap (€)</label>
          <input
            type="number"
            name="maxDiscountAmount"
            value={formData.maxDiscountAmount}
            onChange={handleChange}
            min={0}
            step={0.01}
            className={inputClass}
            placeholder="Optional cap"
          />
        </div>
      )}

      {/* Applies To */}
      <div>
        <label className={labelClass}>Applies To</label>
        <select name="appliesTo" value={formData.appliesTo} onChange={handleChange} className={inputClass}>
          <option value="ALL">All Orders</option>
          <option value="FIRST_ORDER">First Order Only</option>
          <option value="SPECIFIC_PRODUCTS">Specific Products</option>
          <option value="SPECIFIC_CATEGORIES">Specific Categories</option>
        </select>
      </div>

      {/* Min Order Amount */}
      <div>
        <label className={labelClass}>Minimum Order Amount (€)</label>
        <input
          type="number"
          name="minOrderAmount"
          value={formData.minOrderAmount}
          onChange={handleChange}
          min={0}
          step={0.01}
          className={inputClass}
          placeholder="No minimum"
        />
      </div>

      {/* Usage Limits */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Total Usage Limit</label>
          <input
            type="number"
            name="usageLimit"
            value={formData.usageLimit}
            onChange={handleChange}
            min={1}
            className={inputClass}
            placeholder="Unlimited"
          />
        </div>
        <div>
          <label className={labelClass}>Per User Limit</label>
          <input
            type="number"
            name="perUserLimit"
            value={formData.perUserLimit}
            onChange={handleChange}
            min={1}
            required
            className={inputClass}
          />
        </div>
      </div>

      {/* Dates */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className={labelClass}>Starts At *</label>
          <input
            type="datetime-local"
            name="startsAt"
            value={formData.startsAt}
            onChange={handleChange}
            required
            className={inputClass}
          />
        </div>
        <div>
          <label className={labelClass}>Expires At</label>
          <input
            type="datetime-local"
            name="expiresAt"
            value={formData.expiresAt}
            onChange={handleChange}
            className={inputClass}
          />
        </div>
      </div>

      {/* Description */}
      <div>
        <label className={labelClass}>Description</label>
        <textarea
          name="description"
          value={formData.description}
          onChange={handleChange}
          rows={2}
          className={inputClass}
          placeholder="Internal description..."
        />
      </div>

      {/* Toggles */}
      <div className="flex gap-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="isActive"
            checked={formData.isActive}
            onChange={handleChange}
            className="h-4 w-4 text-indigo-600 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Active</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            name="autoApply"
            checked={formData.autoApply}
            onChange={handleChange}
            className="h-4 w-4 text-indigo-600 rounded border-gray-300"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Auto-apply (no code needed)</span>
        </label>
      </div>

      {/* Submit */}
      <div className="pt-2">
        <button
          type="submit"
          disabled={saveMutation.isPending}
          className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
        >
          {saveMutation.isPending
            ? 'Saving...'
            : promotion
            ? 'Update Promotion'
            : 'Create Promotion'}
        </button>
      </div>
    </form>
  );
};

export default PromotionForm;
